// pages/student/Certificates.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Fade,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Home,
  EmojiEvents,
  School,
  Download,
  Visibility,
  ArrowForward,
  Verified,
  Star,
  CheckCircle,
} from "@mui/icons-material";
import SideBar from "./components/SideBar";
import { postData } from "../../services/Request";
import "../../styles/Course.css";

const Certificates = () => {
  const nav = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user || user === "null") {
      nav("/");
      return;
    }

    try {
      const data = JSON.parse(user);
      setUserInfo(data);

      // Fetch certificates from API
      // For now, using sample data
      const sampleCertificates = [
        {
          id: 1,
          courseName: "Microsoft Dynamics",
          issueDate: "2024-01-15",
          certificateId: "WPT-2024-001",
          status: "verified",
          grade: "A",
          image:
            "https://via.placeholder.com/300x200/2BAD65/ffffff?text=Certificate",
        },
        {
          id: 2,
          courseName: "Software Development",
          issueDate: "2024-02-20",
          certificateId: "WPT-2024-002",
          status: "verified",
          grade: "A+",
          image:
            "https://via.placeholder.com/300x200/A0EF48/1a1a1a?text=Certificate",
        },
        {
          id: 3,
          courseName: "Artificial Intelligence",
          issueDate: "2024-03-10",
          certificateId: "WPT-2024-003",
          status: "verified",
          grade: "A",
          image:
            "https://via.placeholder.com/300x200/FFD93D/1a1a1a?text=Certificate",
        },
      ];

      setCertificates(sampleCertificates);
      setLoading(false);

      // Uncomment this when API is ready
      // postData("getcertificates", { userId: data.userId })
      //   .then((response) => {
      //     if (response.status === true) {
      //       setCertificates(response.certificates || []);
      //     }
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching certificates:", error);
      //   })
      //   .finally(() => setLoading(false));
    } catch (error) {
      console.error("Error parsing user data:", error);
      nav("/");
    }
  }, [nav]);

  const handleDownload = (certificateId) => {
    // Download certificate logic
    console.log("Downloading certificate:", certificateId);
  };

  const handleView = (certificateId) => {
    // View certificate logic
    console.log("Viewing certificate:", certificateId);
  };

  const stats = [
    {
      icon: <EmojiEvents sx={{ fontSize: 24 }} />,
      label: "Total Certificates",
      value: certificates.length,
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.08)",
    },
    {
      icon: <Verified sx={{ fontSize: 24 }} />,
      label: "Verified",
      value: certificates.filter((c) => c.status === "verified").length,
      color: "#A0EF48",
      bgColor: "rgba(160, 239, 72, 0.08)",
    },
    {
      icon: <Star sx={{ fontSize: 24 }} />,
      label: "Average Grade",
      value: "A",
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.08)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(247, 248, 242, 0.8)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#2BAD65" }} />
            <Typography sx={{ mt: 2, color: "rgba(26, 26, 26, 0.5)" }}>
              Loading certificates...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 16 }} />
                  Certificates
                </Typography>
              </Breadcrumbs>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                My Certificates
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(26, 26, 26, 0.4)",
                  mt: 0.5,
                }}
              >
                View and download all your course completion certificates
              </Typography>
            </Grid>

            <Grid item>
              <Chip
                icon={<School sx={{ fontSize: 16 }} />}
                label={`${userInfo?.firstname || "Student"}`}
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  border: "1px solid rgba(43, 173, 101, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderBottom: "1px solid rgba(43, 173, 101, 0.04)",
          }}
        >
          <Grid container spacing={2}>
            {stats.map((stat, index) => (
              <Grid item xs={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    border: "1px solid rgba(43, 173, 101, 0.04)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 20px rgba(43, 173, 101, 0.06)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: stat.bgColor,
                      borderRadius: "50%",
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: stat.color,
                      flexShrink: 0,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: "1.1rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(26, 26, 26, 0.4)",
                        fontSize: "0.7rem",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Certificate Grid */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          {certificates.length > 0 ? (
            <Grid container spacing={3}>
              {certificates.map((certificate, index) => (
                <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                  <Fade in timeout={400 + index * 100}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#ffffff",
                        borderRadius: 3,
                        border: "1px solid rgba(43, 173, 101, 0.06)",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 40px rgba(43, 173, 101, 0.1)",
                          borderColor: "#2BAD65",
                        },
                      }}
                    >
                      {/* Certificate Preview */}
                      <Box
                        sx={{
                          position: "relative",
                          height: 160,
                          backgroundColor: "#2BAD65",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          color: "#ffffff",
                          p: 2,
                          background: `linear-gradient(135deg, #2BAD65, #A0EF48)`,
                        }}
                      >
                        <EmojiEvents sx={{ fontSize: 48, mb: 1 }} />
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, textAlign: "center" }}
                        >
                          Certificate
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {certificate.courseName}
                        </Typography>

                        {/* Verified Badge */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            borderRadius: "50%",
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Verified sx={{ color: "#2BAD65", fontSize: 20 }} />
                        </Box>
                      </Box>

                      <CardContent
                        sx={{
                          p: 2.5,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#1a1a1a",
                            fontSize: "1rem",
                            mb: 1,
                          }}
                        >
                          {certificate.courseName}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}
                        >
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="Verified"
                            size="small"
                            sx={{
                              backgroundColor: "rgba(160, 239, 72, 0.12)",
                              color: "#2BAD65",
                              fontSize: "0.6rem",
                              height: 24,
                            }}
                          />
                          <Chip
                            label={`Grade: ${certificate.grade}`}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(255, 217, 61, 0.12)",
                              color: "#FFD93D",
                              fontSize: "0.6rem",
                              height: 24,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.3)",
                            mb: 2,
                          }}
                        >
                          Issued:{" "}
                          {new Date(certificate.issueDate).toLocaleDateString()}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(26, 26, 26, 0.2)",
                            mb: 2,
                            fontSize: "0.6rem",
                          }}
                        >
                          ID: {certificate.certificateId}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: "auto" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleView(certificate.id)}
                            sx={{
                              flex: 1,
                              color: "#2BAD65",
                              borderColor: "rgba(43, 173, 101, 0.2)",
                              fontSize: "0.7rem",
                              "&:hover": {
                                borderColor: "#2BAD65",
                                backgroundColor: "rgba(43, 173, 101, 0.04)",
                              },
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Download />}
                            onClick={() => handleDownload(certificate.id)}
                            sx={{
                              flex: 1,
                              backgroundColor: "#2BAD65",
                              fontSize: "0.7rem",
                              "&:hover": {
                                backgroundColor: "#239A56",
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 15px rgba(43, 173, 101, 0.3)",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            Download
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                backgroundColor: "#ffffff",
                borderRadius: 3,
                border: "1px solid rgba(43, 173, 101, 0.04)",
              }}
            >
              <EmojiEvents
                sx={{ fontSize: 64, color: "rgba(26, 26, 26, 0.1)", mb: 2 }}
              />
              <Typography variant="h6" sx={{ color: "rgba(26, 26, 26, 0.3)" }}>
                No Certificates Yet
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(26, 26, 26, 0.2)", mt: 1 }}
              >
                Complete your courses to earn certificates
              </Typography>
              <Button
                variant="contained"
                onClick={() => nav("/courses")}
                startIcon={<School />}
                sx={{
                  mt: 3,
                  backgroundColor: "#2BAD65",
                  color: "#ffffff",
                  fontWeight: 600,
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#239A56",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 20px rgba(43, 173, 101, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Explore Courses
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Certificates;
